import React, {useEffect, useState} from 'react';
import clsx from 'clsx';

import {ModalReact, ModalType} from 'common/components/modal';
import {SvgIconReact} from 'common/components/svg-icon';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {trackUpsellEvent, UpsellType} from 'common/report/util/tracking-helpers';
import {getUpsellInfo, Metadata, ModalTrigger, UpsellInfo} from 'common/widgets/in-product-upsell-modal/util';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useThemes} from 'common/contexts/react/themes-context';
import {useTranslation} from 'common/i18n/hooks';
import {Translatable} from 'common/i18n';

import styles from './index.module.styl';

const OLD_PLAN_BUILDER_LINK = `/pricing/plan-builder/`;

type Props = {
  modalTrigger: ModalTrigger;
  open: boolean;
  closeModal: () => void;
  metadata?: Metadata;
};

const MODAL_VERSION = `v2`;

const UPSELL_TYPE_TO_TEXT: Record<UpsellType, Record<string, Translatable>> = {
  [UpsellType.FREE_TO_GROWTH]: {
    ctaText: ({t}) => t(`upsells.modal.free-to-growth-cta`),
    ctaUpgradeLink: OLD_PLAN_BUILDER_LINK,
    ctaExitText: ({t}) => t(`upsells.modal.free-to-growth-cta-exit`),
  },
  [UpsellType.GROWTH_TO_ENTERPRISE]: {
    ctaText: ({t}) => t(`upsells.modal.growth-to-enterprise-cta`),
    ctaUpgradeLink: `/contact-us/sales/`,
    ctaExitText: ({t}) => t(`upsells.modal.growth-to-enterprise-cta-exit`),
  },
  // Default unknown to growth values
  [UpsellType.UNKNOWN]: {
    ctaText: ({t}) => t(`upsells.modal.free-to-growth-cta`),
    ctaUpgradeLink: OLD_PLAN_BUILDER_LINK,
    ctaExitText: ({t}) => t(`upsells.modal.free-to-growth-cta-exit`),
  },
};

export function template({modalTrigger, open, closeModal, metadata = {}}: Props) {
  const currentProject = useCurrentProject();
  const [upsellInfo, setUpsellInfo] = useState<UpsellInfo | null>(null);
  const [orgId, setOrgId] = useState<number | null>(null);
  const themesResource = useThemes();
  const isDarkMode = themesResource.isDarkMode();
  const i18n = useTranslation();
  const {print, t} = i18n;

  // Tracking the display of the modal
  useEffect(() => {
    const model = currentProject?.get();

    if (model?.status === ModelStatus.Success && model.value) {
      const tmpInfo = getUpsellInfo(model.value, modalTrigger, i18n, metadata);
      if (tmpInfo) {
        setUpsellInfo(tmpInfo);
        setOrgId(model.value.organizationId);

        if (open) {
          // These events overlap, but send both to be backwards-compatible.
          trackUpsellEvent(`Upsell Modal Shown`, tmpInfo?.trackingFeatureGate, {
            version: MODAL_VERSION,
            upsellType: tmpInfo?.upsellType,
          });
          trackUpsellEvent(`View Upsell`, tmpInfo?.trackingFeatureGate, {
            version: MODAL_VERSION,
            upsellType: tmpInfo?.upsellType,
          });
        }
      }
    }
  }, [open, currentProject]);

  const handleClickUpgrade = () => {
    if (!upsellInfo) {
      return;
    }
    trackUpsellEvent(`Click Upsell`, upsellInfo.trackingFeatureGate, {
      version: MODAL_VERSION,
      upsellType: upsellInfo.upsellType,
    });
  };

  const handleClose = () => {
    if (upsellInfo) {
      trackUpsellEvent(`Dismiss Upsell`, upsellInfo.trackingFeatureGate, {
        version: MODAL_VERSION,
        upsellType: upsellInfo.upsellType,
      });
    }

    closeModal();
  };

  const GrowthGridItems = () => (
    <>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-bar-chart`} />
        {t(`upsells.modal.reports-and-cohorts`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-gem`} />
        {t(`upsells.modal.spark`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-lock`} />
        {t(`upsells.modal.sharing`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-laptop`} />
        {t(`upsells.modal.email-and-slack`)}
      </div>
    </>
  );

  const EnterpriseGridItems = () => (
    <>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-eye`} />
        {t(`upsells.modal.session-replay`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-clipboard`} />
        {t(`upsells.modal.data-governance`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-plug`} />
        {t(`upsells.modal.data-warehouse-ingestion`)}
      </div>
      <div className={styles.gridItem}>
        <SvgIconReact icon={`brand-hand-holding-heart`} />
        {t(`upsells.modal.premium-support`)}
      </div>
    </>
  );

  let upsellLink = ``;
  if (upsellInfo) {
    if (UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaUpgradeLink === OLD_PLAN_BUILDER_LINK) {
      upsellLink = `/settings/org/${orgId}/plan?show-modal=plan-builder`;
    } else {
      upsellLink = `${UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaUpgradeLink}?org=${orgId}`;
    }
  }

  return upsellInfo ? (
    <ModalReact
      className={styles.modalContainer}
      open={open}
      modalTitle={upsellInfo?.title}
      modalDescription={upsellInfo?.description}
      modalType={ModalType.Modal}
    >
      <div className={styles.modalBody}>
        <p className={styles.modalTitle}>{upsellInfo?.title}</p>
        <p className={styles.modalDescription}>{upsellInfo?.description}</p>
        <div className={clsx(styles.gridContainer, styles.spacedTop)}>
          {upsellInfo.upsellType === UpsellType.GROWTH_TO_ENTERPRISE ? <EnterpriseGridItems /> : <GrowthGridItems />}
        </div>
        <div className={styles.spacedTop}>
          <ButtonReact
            className={clsx(styles.ctaButton, {[styles.darkMode]: isDarkMode})}
            theme={ButtonTheme.Tertiary}
            type={ButtonType.Info}
            href={upsellLink}
            notFocusable
            onClick={handleClickUpgrade}
          >
            <div className={styles.ctaText}>
              {upsellInfo ? print(UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaText) : ``}
            </div>
          </ButtonReact>
          <ButtonReact
            className={clsx(styles.ctaTextButton, {[styles.darkMode]: isDarkMode})}
            theme={ButtonTheme.Tertiary}
            notFocusable
            onClick={handleClose}
          >
            <div>{upsellInfo ? print(UPSELL_TYPE_TO_TEXT[upsellInfo.upsellType].ctaExitText) : ``}</div>
          </ButtonReact>
        </div>
      </div>
    </ModalReact>
  ) : null;
}
